/**
 * A blog's featured image, served at the size a page actually needs.
 *
 * Uploads generate a `_lg` variant capped at 1600px wide with the aspect ratio
 * preserved, which is the same picture as the original and a fraction of the
 * bytes. Rendering the base URL instead shipped whatever the author uploaded,
 * so a 4000px phone photo went to every visitor at full resolution.
 *
 * The fallback is not optional. `_lg` exists only for assets uploaded through
 * our pipeline WITH variant generation on, and that was off for blog images
 * until now, so every image posted before this would 404 on the derived URL.
 * `variantUrl` already returns the base URL untouched for legacy, external and
 * Cloudinary assets; the onError here covers the remaining case it cannot know
 * synchronously, which is a media-hosted asset that simply never had variants
 * generated. See the note in lib/media/url.ts.
 *
 * No aspect ratio is imposed. A blog image answers to no external platform, and
 * both public renderers use `h-auto`, so the picture appears in whatever shape
 * it was uploaded.
 */

import React from 'react';
import { variantUrl } from '@/lib/media/url';

export interface BlogFeaturedImageProps {
  url: string;
  alt: string;
  className?: string;
  /** First image on an article page is usually the LCP element, so let callers opt out of lazy. */
  eager?: boolean;
}

export const BlogFeaturedImage: React.FC<BlogFeaturedImageProps> = ({
  url,
  alt,
  className,
  eager = false,
}) => (
  <img
    src={variantUrl(url, 'lg')}
    alt={alt}
    className={className}
    loading={eager ? 'eager' : 'lazy'}
    decoding="async"
    onError={(e) => {
      const img = e.currentTarget;
      // Guard the loop: if the base URL is what already failed, leave it alone
      // rather than reassigning the same src forever.
      if (img.dataset.variantFallback === 'done') return;
      img.dataset.variantFallback = 'done';
      img.src = url;
    }}
  />
);
